import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  FormControl,
  Button,
  useToast,
} from '@chakra-ui/react';
import { HttpContext } from '../../context/HttpContext';
import { useContext, useState, useEffect } from 'react';
import CustomToast from '../../common/CustomToast';
import { ROLES } from '../../constants/roles';
import { MeContext } from '../../context/MeContext';
import useRolesList from '../../hooks/useRolesList';
import useEntityManagement from '../../hooks/useEntitiesList';
import {
  CustomReactSelect,
  formatOptionLabelSelectRole,
} from '../common/CustomReactSelect';

const ChangeRoleModal = (props) => {
  const { isOpen, onClose, selectedAccount } = props;
  const httpCtx = useContext(HttpContext) || {};
  const { authAxios } = httpCtx;
  const toast = useToast();
  const meCtx = useContext(MeContext) || {};
  const { fetchData } = meCtx;
  const account = meCtx.state?.account || {};
  const entities = meCtx.state?.entities || [];
  const originalRole = selectedAccount.role;
  const originalEntity = entities.find(
    (entity) => entity.id === selectedAccount.entityId
  );
  const [role, setRole] = useState({
    label: ROLES[originalRole]?.label,
    value: ROLES[originalRole]?.value,
  });
  const [entity, setEntity] = useState({
    label: originalEntity?.name || 'All entities',
    value: originalEntity?.id,
  });
  const [entityError, setEntityError] = useState('');
  const rolesList = useRolesList(account.role, entities.length);
  const filterAllEntitiesOption = role?.value === ROLES.ent_admin.value;
  const entitiesList = useEntityManagement(entities, filterAllEntitiesOption);

  useEffect(() => {
    if (
      role?.value === ROLES.org_admin.value ||
      (role?.value === ROLES.ent_admin.value && entity?.value === undefined)
    ) {
      setEntity(null);
    }
  }, [role]);

  const validateEntity = async (entity) => {
    if (role.value === ROLES.ent_admin.value && !entity) {
      setEntityError('Please select an entity');
      return false;
    }
    setEntityError('');
    return true;
  };

  const onEntityChange = async (entity) => {
    setEntity(entity);
    await validateEntity(entity);
  };

  const customOnClose = () => {
    setRole({
      label: ROLES[originalRole]?.label,
      value: ROLES[originalRole]?.value,
    });
    setEntity({
      label: originalEntity?.name,
      value: originalEntity?.id,
    });
    setEntityError('');
    onClose();
  };

  const handleChangeRole = async () => {
    try {
      await authAxios.post(`/accounts/${selectedAccount.id}/update-role`, {
        role: role.value,
        entity_id: entity?.value?.id || entity?.value,
      });
      onClose();
      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Role changed"
            description={'Role changed successfully'}
            onClose={props.onClose}
          />
        ),
      });
      fetchData();
    } catch (error) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Error changing role"
            description={'There was an error changing the role'}
            onClose={props.onClose}
          />
        ),
      });
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={customOnClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Change Role</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <Text fontSize="16px" fontWeight="bold">
              Choose a role you want to change to:
            </Text>
            <CustomReactSelect
              options={rolesList}
              formatOptionLabel={formatOptionLabelSelectRole}
              onChange={(role) => {
                setRole(role);
              }}
              defaultValue={originalRole}
              value={role}
              id="change-role"
            />
          </FormControl>
          {account.role === ROLES.org_admin.value &&
            entities.length > 1 &&
            role?.value !== ROLES.org_admin.value && (
              <>
                <FormControl mt={4}>
                  <Text fontSize="16px" fontWeight="bold">
                    Choose an entity you want to change to:
                  </Text>
                  <CustomReactSelect
                    options={entitiesList}
                    formatOptionLabel={formatOptionLabelSelectRole}
                    onChange={(entity) => {
                      onEntityChange(entity);
                    }}
                    placeholder={entity ? '' : 'Select entity'}
                    defaultValue={originalEntity}
                    value={entity}
                    id="change-entity"
                    isInvalid={!!entityError}
                  />
                  {entityError && (
                    <Text ml={6} pl={4} color="red.500" fontSize="sm">
                      {entityError}
                    </Text>
                  )}
                </FormControl>
              </>
            )}
        </ModalBody>
        <ModalFooter display="flex" gap={4} justifyContent="center">
          <Button
            _hover={{ bg: 'primaryDark08' }}
            onClick={handleChangeRole}
            disabled={
              !role || (role.value === ROLES.ent_admin.value && !entity)
            }
          >
            OK
          </Button>
          <Button variant="secondary" onClick={customOnClose}>
            <Text>Cancel</Text>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeRoleModal;
